import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { Alert } from "@/components/ui/alert";
import { GroupPicker } from "@/components/group-picker";
import { useCreateGroupSet, useUpdateGroupSet, type GroupSet } from "@/lib/groups";

export function GroupSetEditor({
    accountId,
    editing,
    onClose,
}: {
    accountId: string;
    editing: GroupSet | null;
    onClose: () => void;
}) {
    const [name, setName] = useState(editing?.name ?? "");
    const [selected, setSelected] = useState<string[]>(editing?.groupIds ?? []);
    const create = useCreateGroupSet();
    const update = useUpdateGroupSet();

    const pending = create.isPending || update.isPending;
    const error = create.error ?? update.error;

    function handleSave() {
        const done = () => onClose();
        if (editing) {
            update.mutate({ id: editing.id, name, groupIds: selected }, { onSuccess: done });
        } else {
            create.mutate({ accountId, name, groupIds: selected }, { onSuccess: done });
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
            <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
                <div className="flex items-center justify-between border-b border-line px-5 py-4">
                    <h2 className="text-base font-semibold text-ink">
                        {editing ? "Sửa nhóm gửi" : "Tạo nhóm gửi"}
                    </h2>
                    <button onClick={onClose} aria-label="Đóng" className="text-ink-muted hover:text-ink">
                        <X className="size-4" />
                    </button>
                </div>

                <div className="space-y-4 px-5 py-5">
                    {error && <Alert tone="danger">{error.message}</Alert>}

                    <Field label="Tên nhóm gửi" htmlFor="group-set-name">
                        <Input
                            id="group-set-name"
                            placeholder="VD: Khách sỉ miền Bắc"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>

                    <GroupPicker
                        accountId={accountId} selected={selected} onChange={setSelected} />
                </div>

                <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
                    <Button variant="ghost" onClick={onClose}>
                        Huỷ
                    </Button>
                    <Button loading={pending} disabled={!name.trim() || selected.length === 0} onClick={handleSave}>
                        {editing ? "Lưu thay đổi" : "Tạo nhóm gửi"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
